/**
 * Operational kill-switch for taking specific offering slugs offline
 * without flipping their database `status`.
 *
 * Sourced from `process.env.NEXT_PUBLIC_HIDDEN_OFFERING_SLUGS` as a
 * comma-separated list. The `NEXT_PUBLIC_` prefix matters: it lets
 * Next.js bake the value into the client bundle at build time, so
 * client-side response filters (search, autocomplete, etc.) can read
 * the same source of truth as the SSR page gate. On the server, it's
 * still readable from `process.env` at request time.
 *
 * Consumers:
 * - `app/offering/[[...slug]]/page.jsx` + private-canada variant —
 *   SSR 404 + `noindex, nofollow` for anonymous traffic on hidden slugs
 *   (admins, account holder, onboarding team, `?dig=` deep links, and
 *   Builder.io editor preserved).
 * - `src/services/elastic-search.js` — drops hidden offerings from
 *   search results + autocomplete suggestions before they reach
 *   consumers (search results page, top-nav search dropdown, partner
 *   offerings, homepage V2 panels).
 * - `scripts/generate-sitemap.mjs` — excludes hidden slugs from
 *   `sitemap.xml` so Google stops re-discovering them.
 *
 * Long-term replacement: a `hidden` boolean on the offering itself,
 * surfaced by primary_facade, that retires this env var and also
 * covers mobile apps + the other webapps (out of reach of this
 * FE-only lever). Tracked as a follow-up on PD-35613.
 */

const ENV_VAR_NAME = 'NEXT_PUBLIC_HIDDEN_OFFERING_SLUGS';

function parseList() {
  const raw = process.env[ENV_VAR_NAME];
  if (!raw) return [];
  return raw
    .split(',')
    .map((slug) => slug.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Returns true when `slug` matches an entry in the hidden-slugs env var.
 * Case-insensitive on both sides.
 */
export function isHiddenSlug(slug) {
  if (typeof slug !== 'string' || slug.length === 0) return false;
  const list = parseList();
  if (list.length === 0) return false;
  return list.includes(slug.toLowerCase());
}

/**
 * Removes hidden offerings from an array of offering-shaped objects.
 *
 * Skips/leaves untouched any non-array input, returns the same instance
 * when nothing needs to be dropped (zero allocations on the hot path
 * when the env var is unset, which is the common case), and only
 * allocates a new array when items actually need to be removed.
 *
 * Items without a string `slug` field are passed through untouched so
 * callers can pipe raw service responses without pre-validation.
 *
 * @param {Array<{slug?: string}>} offerings
 * @returns {Array}
 */
export function filterHiddenOfferings(offerings) {
  if (!Array.isArray(offerings) || offerings.length === 0) return offerings;
  const list = parseList();
  if (list.length === 0) return offerings;
  const hidden = new Set(list);
  return offerings.filter(
    (offering) => typeof offering?.slug !== 'string' || !hidden.has(offering.slug.toLowerCase()),
  );
}
